import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import styled, { useTheme } from 'styled-components'
import { Typo, TypoVariant } from '@/components/common/typography'
import { ScrollContainer } from '@/components/domains/etc'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import useSimilarProductContainerHook from '@/containers/hooks/similar-product.container.hook'
import { GraphQLProductType, transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { decodeId, encodeId } from '@/utils/utilApi'

const SimilarProductTitle = '이 상품과 비슷한 상품'

interface ProductDetailSimilarProductListProps {
  discountRate: number
  productId: number
  isRealSoldOut: boolean
  productName: string
  price: number
  salePrice: number
  reviewScore: number
  reviewCount: number
}

export const ProductDetailSimilarProductList = ({
  discountRate,
  productId,
  isRealSoldOut,
  productName,
  price,
  salePrice,
  reviewScore,
  reviewCount,
}: ProductDetailSimilarProductListProps) => {
  const { trackViewProductDetailBottomProductFirst, trackClickProductDetailBottomProduct } =
    useProductDetailEventTracker()
  const { ref, inView } = useInView({
    triggerOnce: true,
  })

  const { color } = useTheme()

  const { recommendedSimilarProducts } = useSimilarProductContainerHook(
    encodeId('ProductType', productId),
    20
  ) as unknown as {
    loading: boolean
    recommendedSimilarProducts: GraphQLProductType[]
  }

  useEffect(() => {
    if (inView) {
      trackViewProductDetailBottomProductFirst({
        listType: 'similarproduct',
      })
    }
  }, [inView])

  const handleClickProductCard = ({ item, listIndex }: { item: GraphQLProductType; listIndex: number }) => {
    trackClickProductDetailBottomProduct({
      discountRate,
      productId,
      isRealSoldOut,
      productName,
      price,
      salePrice,
      reviewScore,
      reviewCount,
      listType: 'similarproduct',
      listIndex,
      clickedProduct: {
        discountRate: item.discountRate,
        productId: +decodeId(item.id).id,
        isRealSoldOut: item.isRealSoldOut,
        productName: item.name,
        price: item.price,
        salePrice: item.customerPrice,
        reviewScore: item.averageReviewScore,
        reviewCount: item.reviewCount,
      },
    })
  }

  if (!recommendedSimilarProducts?.length) {
    return null
  }

  return (
    <ProductDetailSimilarProductListWrapper className="product-detail-similar-product-list" ref={ref}>
      <div className="product-detail-similar-product-list__header">
        <Typo variant={TypoVariant.Heading6Bold} color={color.gray900}>
          {SimilarProductTitle}
        </Typo>
      </div>
      <ScrollContainer hideRightButton={recommendedSimilarProducts.length < 3} buttonPositionY={30}>
        <div className="product-detail-similar-product-list__list">
          {recommendedSimilarProducts.map((product, index) => {
            const productData = transformProductTypeDefinedByFrontend(product)
            return (
              <div className="product-detail-similar-product-list__item" key={productData.id}>
                <NewProductCard
                  product={productData}
                  size={PRODUCT_CARD_SIZES.lg}
                  onClickProductCard={() => handleClickProductCard({ item: product, listIndex: index })}
                />
              </div>
            )
          })}
        </div>
      </ScrollContainer>
    </ProductDetailSimilarProductListWrapper>
  )
}
const ProductDetailSimilarProductListWrapper = styled.div`
  padding: 2.4rem 0;
  .product-detail-similar-product-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 1.6rem;
    }
    &__list {
      margin-top: 1.6rem;
      display: grid;
      grid-auto-flow: column;
      grid-template-columns: repeat(3, 1fr);
      grid-column-gap: 0.8rem;
      & > *:first-child {
        padding-left: 1.6rem;
      }
      & > *:last-child {
        padding-right: 1.6rem;
      }
    }
  }
`
